import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import _ from "lodash";
import { baseURL } from "../../constants/endPoints";
import { USER_INFO_KEY } from "../../constants/common";
import { getLocalStorage } from "../../helpers/common";
import ActiveBuyerReportView from "./ActiveBuyerReportView";
import { adaptApiResponse } from "./adaptApiResponse";
import "./ActiveBuyerReport.scss";

const trimBase = (url) => (url || "").replace(/\/+$/, "");

const buildEndpoint = (inventoryId) =>
  `${trimBase(baseURL)}/api/v2/active-buyer-report/${encodeURIComponent(inventoryId)}/data`;

const getAccessToken = () => {
  try {
    const info = getLocalStorage(USER_INFO_KEY);
    return _.get(info, "token.accessToken", null);
  } catch (err) {
    return null;
  }
};

const fetchReportData = async (inventoryId, { signal } = {}) => {
  const url = buildEndpoint(inventoryId);
  const accessToken = getAccessToken();

  const headers = {
    "Content-Type": "application/json",
    "api-key": process.env.REACT_APP_REST_API_KEY || ""
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response;
  try {
    response = await fetch(url, { headers, signal });
  } catch (networkErr) {
    if (networkErr?.name === "AbortError") throw networkErr;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[ActiveBuyerReport] network error, falling back to fixture:", networkErr);
      const fixture = await import("./__fixtures__/sampleData.json");
      return fixture.default || fixture;
    }
    const err = new Error("Network error");
    err.status = 0;
    throw err;
  }

  if (response.status === 404) {
    const err = new Error("Property not found");
    err.status = 404;
    throw err;
  }
  if (!response.ok) {
    let apiMessage = "";
    try {
      const body = await response.json();
      apiMessage = body?.message || "";
    } catch (_e) {
      // body wasn't JSON
    }
    const err = new Error(
      apiMessage
        ? `Failed to load report (${response.status}): ${apiMessage}`
        : `Failed to load report (${response.status})`
    );
    err.status = response.status;
    throw err;
  }

  const body = await response.json();
  // API wraps payload as { message, errorCode, result }
  const result = body && Object.prototype.hasOwnProperty.call(body, "result") ? body.result : body;
  if (!result) {
    const err = new Error("Empty report payload");
    err.status = 500;
    throw err;
  }
  return result;
};

const LoadingSkeleton = () => (
  <div className="abr-page-loading" role="status" aria-live="polite">
    <div className="abr-page-loading-inner">
      <div className="abr-skel-bar abr-skel-bar-lg" />
      <div className="abr-skel-bar abr-skel-bar-md" />
      <div className="abr-skel-grid">
        <div className="abr-skel-block" />
        <div className="abr-skel-block" />
        <div className="abr-skel-block" />
      </div>
      <div className="abr-skel-bar abr-skel-bar-sm" />
      <span className="abr-sr-only">Loading active buyer report</span>
    </div>
  </div>
);

const NotFoundState = ({ inventoryId }) => (
  <div className="abr-page-state" role="alert">
    <div className="abr-page-state-inner">
      <h1>Report not available</h1>
      <p>
        We couldn't find an active listing for ID <strong>{inventoryId}</strong>. The listing may have been
        removed or you may not have permission to view this report.
      </p>
      <a className="abr-page-state-link" href="/my-inventory">Back to My Inventory</a>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="abr-page-state" role="alert">
    <div className="abr-page-state-inner">
      <h1>Something went wrong</h1>
      <p>{message || "We hit a snag loading this report. Please try again."}</p>
      <button type="button" className="abr-page-state-btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  </div>
);

const ActiveBuyerReport = () => {
  const { inventoryId } = useParams();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get("edit") === "1";
  const mode = editMode ? "agent" : "seller";

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!inventoryId) {
      setError({ status: 404, message: "Missing inventory id" });
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReportData(inventoryId, { signal: controller.signal })
      .then((data) => {
        if (cancelled) return;
        setReportData(adaptApiResponse(data));
      })
      .catch((err) => {
        if (cancelled || err?.name === "AbortError") return;
        setError({ status: err?.status || 0, message: err?.message || "Failed to load report" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [inventoryId, attempt]);

  if (loading) return <LoadingSkeleton />;
  if (error?.status === 404) return <NotFoundState inventoryId={inventoryId} />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!reportData) return <ErrorState message="No report data returned" onRetry={refetch} />;

  return <ActiveBuyerReportView reportData={reportData} mode={mode} inventoryId={inventoryId} />;
};

export default ActiveBuyerReport;
